import React from 'react';
import { motion } from 'framer-motion';
import { useCvData } from '../context/CvDataContext.js';
import './Experience.css';

const Experience = ({ onExperienceClick }) => {
  const { experience, loading, error, reload } = useCvData();
  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.split(/\s|-/).filter(Boolean);
    const initials = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
    return initials.toUpperCase();
  };

  if (error) return (
    <section className="experience-section" id="experience">
      <h2>Experience</h2>
      <p role="alert">{error.message || 'Failed to load.'}</p>
      <button onClick={reload} className="retry-btn">Retry</button>
    </section>
  );
  if (loading) return (
    <section className="experience-section" id="experience">
      <h2>Experience</h2>
      <div className="experience-timeline">
        {[...Array(4)].map((_, idx) => (
          <div key={idx} className="experience-item" style={{ pointerEvents: 'none' }}>
            <div className="experience-header">
              <div className="skeleton skeleton-circle experience-logo" />
              <div style={{ flex: 1 }}>
                <div className="skeleton skeleton-text lg" style={{ width: '50%', marginBottom: 8 }} />
                <div className="skeleton skeleton-text" style={{ width: '40%' }} />
              </div>
              <div className="skeleton skeleton-text" style={{ width: 100 }} />
            </div>
            <div className="tech-stack" style={{ marginTop: 12 }}>
              {[...Array(4)].map((__, j) => (
                <span key={j} className="skeleton" style={{ display: 'inline-block', width: 80, height: 20, marginRight: 8, borderRadius: 12 }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
  if (experience.length === 0) return null;
  return (
    <section className="experience-section" id="experience">
      <h2>Experience</h2>
      <div className="experience-timeline">
        {experience.map((exp, index) => (
          <motion.div 
            key={exp.id} 
            className="experience-item"
            onClick={() => onExperienceClick(exp)}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ 
              duration: 0.6, 
              delay: index * 0.1,
              type: "spring",
              stiffness: 100,
              damping: 15
            }}
            whileHover={{ 
              scale: 1.02,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="experience-header">
              {exp.logo ? (
                <img className="experience-logo" src={exp.logo} alt={`${exp.company} logo`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <span className="company-fallback experience-logo" aria-hidden>
                  {getInitials(exp.company)}
                </span>
              )}
              <h3>{exp.title}</h3>
              <span className="company">{exp.company}</span>
              <span className="period">{exp.period}</span>
            </div>
            <div className="tech-stack">
              {exp.technologies.map((tech, techIndex) => (
                <span key={techIndex} className="tech">{tech}</span>
              ))}
            </div>
            {exp.media.images.length > 0 && (
              <div className="media-indicator">
                <i className="fas fa-images"></i>
                <span>{exp.media.images.length} image{exp.media.images.length !== 1 ? 's' : ''}</span>
              </div>
            )}
            {exp.media.videos.length > 0 && (
              <div className="media-indicator">
                <i className="fas fa-video"></i>
                <span>{exp.media.videos.length} video{exp.media.videos.length !== 1 ? 's' : ''}</span>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Experience;
